/** Portal leaf fractal — mountable, with a quieter “subtle” intensity for Club pages. */

export type LeafIntensity = "full" | "subtle";

export type LeafFractalHandle = {
  destroy: () => void;
  setAccent: (rgbaGreen?: string) => void;
};

type Opts = {
  intensity?: LeafIntensity;
  /** Optional green override e.g. chapter stain */
  accent?: string;
};

type Pt = { x: number; y: number };

export function mountLeafFractal(
  canvas: HTMLCanvasElement,
  opts: Opts = {},
): LeafFractalHandle {
  const intensity = opts.intensity ?? "subtle";
  const subtle = intensity === "subtle";
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return { destroy: () => undefined, setAccent: () => undefined };
  }

  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const TAU = Math.PI * 2;
  const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));
  const lerp = (a: number, b: number, u: number) => a + (b - a) * u;
  const lerpPt = (a: Pt, b: Pt, u: number): Pt => ({
    x: lerp(a.x, b.x, u),
    y: lerp(a.y, b.y, u),
  });
  const rot = (x: number, y: number, a: number): Pt => ({
    x: x * Math.cos(a) + y * Math.sin(a),
    y: -x * Math.sin(a) + y * Math.cos(a),
  });
  const hash = (n: number) => {
    const s = Math.sin(n * 127.1) * 43758.5453;
    return s - Math.floor(s);
  };
  const noise2 = (x: number, y: number) => {
    const ix = Math.floor(x);
    const iy = Math.floor(y);
    const fx = x - ix;
    const fy = y - iy;
    const ux = fx * fx * (3 - 2 * fx);
    const uy = fy * fy * (3 - 2 * fy);
    const a = hash(ix * 19.1 + iy * 47.3);
    const b = hash((ix + 1) * 19.1 + iy * 47.3);
    const c = hash(ix * 19.1 + (iy + 1) * 47.3);
    const d = hash((ix + 1) * 19.1 + (iy + 1) * 47.3);
    return lerp(lerp(a, b, ux), lerp(c, d, ux), uy) * 2 - 1;
  };
  const curl = (x: number, y: number, time: number) => {
    const e = 0.015;
    return {
      x: (noise2(x, y + e + time) - noise2(x, y - e + time)) / (2 * e),
      y: -(noise2(x + e + time * 0.7, y) - noise2(x - e + time * 0.7, y)) / (2 * e),
    };
  };

  // Visual multipliers — subtle = quieter portal leaf
  const M = subtle
    ? {
        alpha: 0.42,
        glow: 0.32,
        link: 0.38,
        packet: 0.45,
        shadow: 0.4,
        speed: 0.72,
        scale: 0.68,
        trailMax: 3,
        centerY: 0.58,
      }
    : {
        alpha: 1,
        glow: 1,
        link: 1,
        packet: 1,
        shadow: 1,
        speed: 1,
        scale: 0.78,
        trailMax: 5,
        centerY: 0.53,
      };

  let accent = opts.accent || "143, 191, 138";
  const setAccent = (hexOrRgb?: string) => {
    if (!hexOrRgb) {
      accent = "143, 191, 138";
      return;
    }
    if (hexOrRgb.includes(",")) {
      accent = hexOrRgb;
      return;
    }
    const h = hexOrRgb.replace("#", "");
    if (h.length === 6) {
      accent = `${parseInt(h.slice(0, 2), 16)}, ${parseInt(h.slice(2, 4), 16)}, ${parseInt(h.slice(4, 6), 16)}`;
    }
  };
  setAccent(opts.accent);

  const leaflets = [
    { angle: 0, length: 1, width: 0.2, peak: 0.42, center: true },
    { angle: 0.73, length: 0.86, width: 0.175, peak: 0.42, center: false },
    { angle: 1.36, length: 0.64, width: 0.15, peak: 0.4, center: false },
    { angle: 2.18, length: 0.4, width: 0.12, peak: 0.38, center: false },
  ];
  const stem = { top: 0, bottom: -0.26, halfW: 0.026 };
  const edgeDiv = 3;
  const kite = (leaflet: (typeof leaflets)[0]) => ({
    hub: rot(0, 0, leaflet.angle),
    right: rot(leaflet.width, leaflet.length * leaflet.peak, leaflet.angle),
    tip: rot(0, leaflet.length, leaflet.angle),
    left: rot(-leaflet.width, leaflet.length * leaflet.peak, leaflet.angle),
  });

  const buildHomes = () => {
    const points: { x: number; y: number; role: string; leaflet: number }[] = [];
    const push = (p: Pt, role: string, leaflet = 0) =>
      points.push({ x: Math.max(0, p.x), y: p.y, role, leaflet });

    push({ x: 0, y: 0 }, "hub", -1);

    leaflets.forEach((leaflet, index) => {
      const shape = kite(leaflet);
      if (leaflet.center) {
        for (let s = 1; s <= edgeDiv; s += 1) {
          push(lerpPt(shape.hub, shape.right, s / (edgeDiv + 1)), "edge", index);
        }
        push(shape.right, "waist", index);
        for (let s = 1; s <= edgeDiv; s += 1) {
          push(lerpPt(shape.right, shape.tip, s / (edgeDiv + 1)), "edge", index);
        }
        push(shape.tip, "tip", index);
        for (let s = 1; s <= edgeDiv + 1; s += 1) {
          push(lerpPt(shape.hub, shape.tip, s / (edgeDiv + 2)), "vein", index);
        }
        for (let s = 1; s <= edgeDiv; s += 1) {
          const p = lerpPt(shape.tip, shape.left, s / (edgeDiv + 1));
          push({ x: Math.abs(p.x) * 0.15, y: p.y }, "edge", index);
        }
      } else {
        for (let s = 1; s <= edgeDiv; s += 1) {
          push(lerpPt(shape.hub, shape.right, s / (edgeDiv + 1)), "edge", index);
        }
        push(shape.right, "waist", index);
        for (let s = 1; s <= edgeDiv; s += 1) {
          push(lerpPt(shape.right, shape.tip, s / (edgeDiv + 1)), "edge", index);
        }
        push(shape.tip, "tip", index);
        for (let s = 1; s <= edgeDiv; s += 1) {
          const p = lerpPt(shape.tip, shape.left, s / (edgeDiv + 1));
          push(
            p.x >= 0
              ? p
              : rot(leaflet.width * 0.4, leaflet.length * (0.5 + s * 0.08), leaflet.angle),
            "edge",
            index,
          );
        }
        for (let s = 1; s <= edgeDiv + 1; s += 1) {
          push(lerpPt(shape.hub, shape.tip, s / (edgeDiv + 2)), "vein", index);
        }
      }
    });

    push({ x: stem.halfW, y: stem.top }, "stem", -1);
    push({ x: stem.halfW, y: stem.bottom * 0.45 }, "stem", -1);
    push({ x: stem.halfW * 0.7, y: stem.bottom }, "stem", -1);
    push({ x: 0, y: stem.bottom }, "stem", -1);
    return points;
  };

  const particles = buildHomes().map((p, i) => ({
    x: p.x,
    y: p.y,
    vx: 0,
    vy: 0,
    homeX: p.x,
    homeY: p.y,
    role: p.role,
    leaflet: p.leaflet,
    phase: hash(i * 5.13 + 2.1) * TAU,
    size:
      p.role === "hub"
        ? 2.6
        : p.role === "tip"
          ? 2
          : p.role === "waist"
            ? 1.55
            : p.role === "stem"
              ? 1.2
              : 1.1,
    trail: [] as Pt[],
  }));

  const packets = Array.from({ length: subtle ? 4 : 7 }, (_, i) => ({
    leaflet: i % 4,
    u: hash(i * 9.1),
    speed: 0.18 + hash(i * 2.3) * 0.22,
    phase: hash(i * 4.4) * TAU,
  }));

  let width = 0;
  let height = 0;
  let time = 0;
  let running = true;
  let raf = 0;
  let last = performance.now();
  let centerX = 0;
  let centerY = 0;
  let scale = 1;
  let scaleX = 1;
  let scaleY = 1;
  let resizeTimer = 0;

  const toScreen = (x: number, y: number) => ({
    x: centerX + x * scale * scaleX,
    y: centerY - y * scale * scaleY,
  });

  const resize = () => {
    const parent = canvas.parentElement;
    const rect = (parent ?? canvas).getBoundingClientRect();
    width = Math.max(280, Math.floor(rect.width || window.innerWidth));
    height = Math.max(360, Math.floor(rect.height || window.innerHeight));
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };

  const step = (dt: number) => {
    if (reduceMotion) return;
    const breath = Math.sin(time * 0.5);
    const secondBreath = Math.sin(time * 0.83 + 1.1);
    scaleX = 1 + breath * 0.08 + secondBreath * 0.03;
    scaleY = 1 - breath * 0.05 + Math.cos(time * 0.4) * 0.08;

    particles.forEach((p) => {
      const wave =
        p.leaflet >= 0
          ? Math.sin(time * 1.7 + p.leaflet * 0.85 + p.phase) * 0.016
          : 0;
      const flow = curl(p.x * 1.7, p.y * 1.7, time * 0.25 + p.phase * 0.05);
      const orbit = 0.02 * Math.sin(time * 0.7 + p.phase);
      const spring = 2.8;
      const ax = (p.homeX - p.x) * spring + flow.x * 0.055 - p.y * orbit;
      const ay = (p.homeY - p.y) * spring + flow.y * 0.055 + p.x * orbit * 0.4 + wave;

      p.vx = p.vx * 0.9 + ax * dt;
      p.vy = p.vy * 0.9 + ay * dt;
      p.x += p.vx;
      p.y += p.vy;

      if (p.role === "hub") {
        p.x = 0;
        p.vx = 0;
      } else if (p.x < 0) {
        p.x = -p.x;
        p.vx = Math.abs(p.vx);
      }

      p.trail.push({ x: p.x, y: p.y });
      if (p.trail.length > M.trailMax) p.trail.shift();
    });

    packets.forEach((packet) => {
      packet.u += packet.speed * dt;
      if (packet.u > 1) packet.u -= 1;
    });
  };

  const drawSilhouette = () => {
    leaflets.forEach((leaflet) => {
      const shape = kite(leaflet);
      const polygons = [[shape.hub, shape.right, shape.tip, shape.left]];
      if (!leaflet.center) {
        polygons.push([
          { x: -shape.hub.x, y: shape.hub.y },
          { x: -shape.right.x, y: shape.right.y },
          { x: -shape.tip.x, y: shape.tip.y },
          { x: -shape.left.x, y: shape.left.y },
        ]);
      }
      polygons.forEach((polygon) => {
        ctx.beginPath();
        polygon.forEach((point, i) => {
          const p = toScreen(point.x, point.y);
          if (i === 0) ctx.moveTo(p.x, p.y);
          else ctx.lineTo(p.x, p.y);
        });
        ctx.closePath();
        ctx.strokeStyle = `rgba(${accent}, ${0.28 * M.alpha})`;
        ctx.lineWidth = 1.25;
        ctx.stroke();
      });
    });

    const top = toScreen(0, stem.top);
    const bottom = toScreen(0, stem.bottom);
    ctx.beginPath();
    ctx.moveTo(top.x, top.y);
    ctx.lineTo(bottom.x, bottom.y);
    ctx.strokeStyle = `rgba(196, 176, 122, ${0.32 * M.alpha})`;
    ctx.lineWidth = 1.4;
    ctx.stroke();
  };

  const strokeMirror = (
    ax: number,
    ay: number,
    bx: number,
    by: number,
    color: string,
    lineWidth: number,
  ) => {
    ctx.beginPath();
    ctx.moveTo(ax, ay);
    ctx.lineTo(bx, by);
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(2 * centerX - ax, ay);
    ctx.lineTo(2 * centerX - bx, by);
    ctx.stroke();
  };

  const drawNode = (x: number, y: number, p: (typeof particles)[0]) => {
    const glow = 0.7 + Math.sin(time * 2.4 + p.phase) * 0.28;
    const boost =
      p.role === "hub" ? 1.45 : p.role === "tip" ? 1.2 : p.role === "waist" ? 1.08 : 1;
    const radius = p.size * 0.82 * glow * boost * (subtle ? 0.85 : 1);

    ctx.beginPath();
    ctx.arc(x, y, radius * 2.2, 0, TAU);
    ctx.fillStyle =
      p.role === "hub"
        ? `rgba(${accent}, ${0.12 * glow * M.glow})`
        : `rgba(${accent}, ${0.06 * glow * M.glow})`;
    ctx.fill();

    ctx.beginPath();
    ctx.arc(x, y, radius, 0, TAU);
    ctx.fillStyle =
      p.role === "hub" ? `rgba(159, 212, 160, ${0.9 * M.alpha})` : `rgba(240, 248, 236, ${0.75 * M.alpha})`;
    ctx.shadowColor =
      p.role === "hub"
        ? `rgba(160, 220, 150, ${M.shadow})`
        : `rgba(${accent}, ${0.75 * M.shadow})`;
    ctx.shadowBlur = (p.role === "hub" ? 32 : 12) * M.shadow;
    ctx.fill();
    ctx.shadowBlur = 0;
  };

  const draw = (now: number) => {
    if (!running) return;

    const dt = clamp((now - last) / 1000, 0.001, 0.033) * M.speed;
    last = now;
    time += dt;
    step(dt);

    centerX = width * 0.5;
    centerY = height * M.centerY;
    scale = Math.min(width, height) * M.scale;
    ctx.clearRect(0, 0, width, height);
    drawSilhouette();

    const right = particles.map((p) => {
      const wave = p.leaflet >= 0 ? Math.sin(time * 2 + p.leaflet * 0.9) * 0.004 : 0;
      return { ...toScreen(p.x, p.y + wave), p };
    });

    right.forEach(({ p }) => {
      if (p.trail.length < 2) return;
      for (let k = 1; k < p.trail.length; k += 1) {
        const a = toScreen(p.trail[k - 1].x, p.trail[k - 1].y);
        const b = toScreen(p.trail[k].x, p.trail[k].y);
        strokeMirror(
          a.x,
          a.y,
          b.x,
          b.y,
          `rgba(${accent}, ${(k / p.trail.length) * 0.16 * M.link})`,
          0.8,
        );
      }
    });

    const linkDistance = scale * 0.28 * (1 + Math.sin(time * 0.45) * 0.04);
    for (let i = 0; i < right.length; i += 1) {
      for (let j = i + 1; j < right.length; j += 1) {
        const a = right[i];
        const b = right[j];
        const distance = Math.hypot(a.x - b.x, a.y - b.y);
        const sameLeaf = a.p.leaflet >= 0 && a.p.leaflet === b.p.leaflet ? 1.45 : 1;
        const crossPenalty =
          a.p.leaflet >= 0 && b.p.leaflet >= 0 && a.p.leaflet !== b.p.leaflet ? 0.72 : 1;
        const maxDistance = linkDistance * sameLeaf * crossPenalty;
        if (distance < maxDistance) {
          strokeMirror(
            a.x,
            a.y,
            b.x,
            b.y,
            `rgba(${accent}, ${(1 - distance / maxDistance) * 0.48 * M.link})`,
            1.05,
          );
        }
      }
    }

    const hub = right.find((point) => point.p.role === "hub") || right[0];
    right.forEach((point) => {
      if (point.p.role !== "tip" && point.p.role !== "waist") return;
      strokeMirror(
        hub.x,
        hub.y,
        point.x,
        point.y,
        `rgba(196, 176, 122, ${0.15 * M.alpha})`,
        point.p.role === "tip" ? 1.25 : 0.95,
      );
    });

    packets.forEach((packet) => {
      const leaflet = leaflets[Math.min(packet.leaflet, leaflets.length - 1)];
      const tip = rot(0, leaflet.length * packet.u, leaflet.angle);
      const point = toScreen(tip.x, tip.y);
      const glow = 0.4 + 0.4 * Math.sin(time * 3 + packet.phase);
      const drawPacket = (x: number, y: number) => {
        ctx.beginPath();
        ctx.arc(x, y, 2.2 * glow * (subtle ? 0.8 : 1), 0, TAU);
        ctx.fillStyle = `rgba(220, 245, 210, ${0.55 * glow * M.packet})`;
        ctx.shadowColor = `rgba(180, 230, 150, ${0.9 * M.shadow})`;
        ctx.shadowBlur = 12 * M.shadow;
        ctx.fill();
        ctx.shadowBlur = 0;
      };
      drawPacket(point.x, point.y);
      if (!leaflet.center) drawPacket(2 * centerX - point.x, point.y);
    });

    right.forEach((point) => {
      if (point.p.role !== "waist") return;
      const alpha = (0.06 + 0.05 * Math.sin(time * 2.2 + point.p.phase)) * M.alpha;
      ctx.beginPath();
      ctx.moveTo(point.x, point.y);
      ctx.lineTo(2 * centerX - point.x, point.y);
      ctx.strokeStyle = `rgba(${accent}, ${alpha})`;
      ctx.stroke();
    });

    right.forEach(({ x, y, p }) => {
      drawNode(x, y, p);
      if (p.role !== "hub") drawNode(2 * centerX - x, y, p);
    });

    if (!reduceMotion) raf = requestAnimationFrame(draw);
  };

  const onResize = () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(resize, 120);
  };
  const onVisibility = () => {
    running = !document.hidden;
    if (running) {
      last = performance.now();
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(draw);
    }
  };

  resize();
  last = performance.now();
  if (reduceMotion) draw(last);
  else raf = requestAnimationFrame(draw);

  window.addEventListener("resize", onResize);
  document.addEventListener("visibilitychange", onVisibility);

  return {
    setAccent,
    destroy: () => {
      running = false;
      cancelAnimationFrame(raf);
      window.clearTimeout(resizeTimer);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", onVisibility);
      ctx.clearRect(0, 0, width, height);
    },
  };
}
